(() => {
  'use strict';

  const root = document.querySelector('[data-sa-cookie-consent]');
  if (!root) return;

  const panel = root.querySelector('.sa-consent-v16__panel');
  const summary = root.querySelector('[data-sa-consent-summary]');
  const form = root.querySelector('[data-sa-consent-form]');
  const analyticsInput = root.querySelector('[data-sa-consent-analytics]');
  const marketingInput = root.querySelector('[data-sa-consent-marketing]');
  const preferencesInput = root.querySelector('[data-sa-consent-preferences]');
  const errorMessage = root.querySelector('[data-sa-consent-error]');
  const pixelId = root.dataset.tiktokPixelId;
  const marketCountry = (root.dataset.marketCountry || '').slice(0, 2).toUpperCase();
  const explicitConsentCountries = new Set([
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
    'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL',
    'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'CH', 'GB'
  ]);
  let privacyApi = null;
  let tiktokLoaded = false;

  const nativeBannerVisible = () => Array.from(
    document.querySelectorAll('#shopify-pc__banner, .shopify-pc__banner__dialog, [data-shopify-cookie-banner]')
  ).some((element) => {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return !element.hidden
      && element.getAttribute('aria-hidden') !== 'true'
      && style.display !== 'none'
      && style.visibility !== 'hidden'
      && Number(style.opacity || 1) !== 0
      && rect.width > 0
      && rect.height > 0;
  });

  const getConsent = () => {
    if (!privacyApi?.currentVisitorConsent) return {};
    return privacyApi.currentVisitorConsent() || {};
  };

  const getCountry = () => {
    const apiRegion = privacyApi?.getRegion?.() || '';
    return apiRegion.slice(0, 2).toUpperCase() || marketCountry;
  };

  const requiresExplicitConsent = () => explicitConsentCountries.has(getCountry());

  const hasConsentDecision = () => {
    const consent = getConsent();
    return ['analytics', 'marketing', 'preferences'].some((key) => (
      consent[key] === 'yes' || consent[key] === 'no' || consent[key] === true || consent[key] === false
    ));
  };

  const isAllowed = (key) => {
    const consent = getConsent();
    if (consent[key] === 'yes' || consent[key] === true) return true;
    if (consent[key] === 'no' || consent[key] === false) return false;
    if (requiresExplicitConsent()) return false;
    const method = {
      analytics: 'analyticsProcessingAllowed',
      marketing: 'marketingAllowed',
      preferences: 'preferencesProcessingAllowed'
    }[key];
    return Boolean(method && privacyApi?.[method]?.());
  };

  const loadTikTok = () => {
    if (!pixelId || !isAllowed('marketing')) return;
    if (tiktokLoaded) {
      window.ttq?.grantConsent?.();
      window.ttq?.enableCookie?.();
      return;
    }

    ((windowObject, documentObject, objectName) => {
      windowObject.TiktokAnalyticsObject = objectName;
      const queue = windowObject[objectName] = windowObject[objectName] || [];
      queue.methods = [
        'page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once',
        'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent',
        'revokeConsent', 'grantConsent'
      ];
      queue.setAndDefer = (target, method) => {
        target[method] = (...args) => target.push([method].concat(args));
      };
      queue.methods.forEach((method) => queue.setAndDefer(queue, method));
      queue.instance = (id) => {
        const instance = queue._i?.[id] || [];
        queue.methods.forEach((method) => queue.setAndDefer(instance, method));
        return instance;
      };
      queue.load = (id, options) => {
        const url = 'https://analytics.tiktok.com/i18n/pixel/events.js';
        queue._i = queue._i || {};
        queue._i[id] = [];
        queue._i[id]._u = url;
        queue._t = queue._t || {};
        queue._t[id] = Date.now();
        queue._o = queue._o || {};
        queue._o[id] = options || {};
        const script = documentObject.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `${url}?sdkid=${encodeURIComponent(id)}&lib=${objectName}`;
        documentObject.head.appendChild(script);
      };
    })(window, document, 'ttq');

    window.ttq.load(pixelId);
    window.ttq.grantConsent?.();
    window.ttq.page();
    tiktokLoaded = true;
  };

  const syncMarketing = () => {
    if (isAllowed('marketing')) {
      loadTikTok();
      return;
    }
    if (window.ttq && tiktokLoaded) {
      window.ttq.revokeConsent?.();
      window.ttq.disableCookie?.();
    }
  };

  const showSummary = (restoreFocus = false) => {
    summary.hidden = false;
    form.hidden = true;
    if (restoreFocus) root.querySelector('[data-sa-consent-settings]')?.focus();
  };

  const showError = () => {
    if (errorMessage) errorMessage.hidden = false;
  };

  const open = (settings = false) => {
    if (nativeBannerVisible()) return;
    root.hidden = false;
    document.body.classList.add('sa-cookie-consent-open');
    if (settings) {
      analyticsInput.checked = isAllowed('analytics');
      marketingInput.checked = isAllowed('marketing');
      preferencesInput.checked = isAllowed('preferences');
      summary.hidden = true;
      form.hidden = false;
    } else {
      showSummary();
    }
    requestAnimationFrame(() => panel?.focus({ preventScroll: true }));
  };

  const close = () => {
    root.hidden = true;
    document.body.classList.remove('sa-cookie-consent-open');
  };

  const save = (settings) => {
    if (!privacyApi?.setTrackingConsent) {
      showError();
      return;
    }
    if (errorMessage) errorMessage.hidden = true;
    try {
      privacyApi.setTrackingConsent(settings, (result) => {
        if (result?.error) {
          showError();
          return;
        }
        close();
        syncMarketing();
        document.dispatchEvent(new CustomEvent('stadiart:consent-saved', { detail: settings }));
      });
    } catch (error) {
      showError();
    }
  };

  root.querySelector('[data-sa-consent-accept]')?.addEventListener('click', () => {
    save({ analytics: true, marketing: true, preferences: true });
  });

  root.querySelectorAll('[data-sa-consent-reject]').forEach((button) => {
    button.addEventListener('click', () => save({ analytics: false, marketing: false, preferences: false }));
  });

  root.querySelector('[data-sa-consent-settings]')?.addEventListener('click', () => open(true));
  root.querySelector('[data-sa-consent-back]')?.addEventListener('click', () => showSummary(true));
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    save({
      analytics: analyticsInput.checked,
      marketing: marketingInput.checked,
      preferences: preferencesInput.checked
    });
  });

  document.addEventListener('click', (event) => {
    const reopen = event.target.closest('[data-sa-consent-reopen]');
    if (!reopen) return;
    event.preventDefault();
    open(true);
  });

  document.addEventListener('visitorConsentCollected', syncMarketing);

  const ready = () => {
    privacyApi = window.Shopify?.customerPrivacy;
    if (!privacyApi) {
      if (explicitConsentCountries.has(marketCountry)) window.setTimeout(() => open(false), 500);
      return;
    }
    syncMarketing();
    const shouldShow = !hasConsentDecision()
      && (Boolean(privacyApi.shouldShowBanner?.()) || requiresExplicitConsent());
    if (shouldShow) window.setTimeout(() => open(false), 500);
  };

  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (!error) {
          ready();
          return;
        }
        if (explicitConsentCountries.has(marketCountry)) window.setTimeout(() => open(false), 500);
      }
    );
  } else if (explicitConsentCountries.has(marketCountry)) {
    window.setTimeout(() => open(false), 500);
  }
})();
